import { createColumnHelper } from '@tanstack/react-table';
import type { ReactElement } from 'react';
import {
  formatAuditActionLabel,
  formatAuditActorTypeLabel,
  formatAuditResourceTypeLabel,
  formatAuditSnapshotSummary,
  formatAuditTimestamp,
  type AuditEntryRecord,
} from './audit-log-helpers.js';
import { AuditChainCell } from './audit-log-filter-bar.js';
import { Badge, Tag } from './components/ui/primitives.js';

export function AuditWhoCell({ entry }: { readonly entry: AuditEntryRecord }): ReactElement {
  return (
    <div className="min-w-0 flex flex-col gap-1 overflow-hidden">
      <span className="truncate font-mono text-[13px] font-medium text-fg" title={entry.actorUid}>
        {entry.actorUid}
      </span>
      <div className="flex items-center gap-1 overflow-hidden">
        <Badge variant="outline" size="sm">
          {formatAuditActorTypeLabel(entry.actorType)}
        </Badge>
        {entry.actorRole && (
          <Badge variant="secondary" size="sm">
            {entry.actorRole}
          </Badge>
        )}
      </div>
    </div>
  );
}

export function AuditWhatCell(props: {
  readonly entry: AuditEntryRecord;
  readonly showWorkspaceBadge?: boolean | undefined;
  readonly workspaceName?: string | undefined;
}): ReactElement {
  const { entry, showWorkspaceBadge = false, workspaceName } = props;
  const actionLabel = formatAuditActionLabel(entry.action);
  return (
    <div className="min-w-0 flex flex-col gap-1 overflow-hidden">
      <div className="flex items-center gap-1.5 overflow-hidden">
        <span className="truncate font-semibold text-fg" title={actionLabel}>
          {actionLabel}
        </span>
        <Badge variant="outline" size="sm">
          {formatAuditResourceTypeLabel(entry.resourceType)}
        </Badge>
        {showWorkspaceBadge && (
          <Tag variant="outline" size="sm" data-testid={`audit-ws-badge-${entry.auditId}`}>
            {workspaceName ?? (entry.workspaceId || 'Organisation')}
          </Tag>
        )}
      </div>
      <div className="flex items-center gap-1.5 overflow-hidden text-[12px] text-fg-secondary">
        <span className="truncate font-mono" title={entry.resourceId}>
          {entry.resourceId}
        </span>
        {entry.reason && entry.reason.trim().length > 0 && (
          <span className="truncate" title={entry.reason}>
            · Reason: {entry.reason}
          </span>
        )}
      </div>
    </div>
  );
}

export function AuditWhenCell({ timestamp }: { readonly timestamp: string }): ReactElement {
  return (
    <span className="whitespace-nowrap font-mono text-[12px] text-fg-secondary tabular-nums">
      {formatAuditTimestamp(timestamp)}
    </span>
  );
}

export function AuditSnapshotCell(props: {
  readonly testId: string;
  readonly snapshot: Readonly<Record<string, unknown>> | null | undefined;
  readonly isAfter?: boolean;
}): ReactElement {
  const summary = formatAuditSnapshotSummary(props.snapshot);
  return (
    <div
      data-testid={props.testId}
      title={summary}
      className={`line-clamp-2 overflow-hidden break-all font-mono text-[12px] leading-snug ${props.isAfter ? 'text-fg' : 'text-fg-secondary'}`}
    >
      {summary}
    </div>
  );
}

export const AUDIT_COLUMN_WIDTHS: Readonly<Record<string, string>> = {
  chain: 'w-[175px]',
  when: 'w-[155px]',
  who: 'w-[180px]',
  what: 'w-[240px]',
  before: 'w-[215px]',
  after: 'w-[215px]',
};

const col = createColumnHelper<AuditEntryRecord>();

export function buildAuditColumns(params?: {
  readonly showWorkspaceBadges?: boolean | undefined;
  readonly workspaceNameMap?: ReadonlyMap<string, string> | undefined;
}) {
  const showWs = Boolean(params?.showWorkspaceBadges);
  const wsMap = params?.workspaceNameMap;
  return [
    col.accessor((r) => r.sequence ?? 0, {
      id: 'chain',
      header: 'Chain',
      cell: (i) => <AuditChainCell entry={i.row.original} />,
    }),
    col.accessor('timestamp', {
      id: 'when',
      header: 'When',
      cell: (i) => <AuditWhenCell timestamp={i.getValue()} />,
    }),
    col.accessor('actorUid', {
      id: 'who',
      header: 'Who',
      cell: (i) => <AuditWhoCell entry={i.row.original} />,
    }),
    col.accessor('action', {
      id: 'what',
      header: 'What',
      cell: (i) => {
        const rec = i.row.original;
        const wsName = rec.workspaceId
          ? (wsMap?.get(rec.workspaceId) ?? rec.workspaceId)
          : 'Organisation';
        return <AuditWhatCell entry={rec} showWorkspaceBadge={showWs} workspaceName={wsName} />;
      },
    }),
    col.accessor((r) => formatAuditSnapshotSummary(r.beforeSnapshot), {
      id: 'before',
      header: 'Before',
      cell: (i) => (
        <AuditSnapshotCell
          testId={`audit-before-${i.row.original.auditId}`}
          snapshot={i.row.original.beforeSnapshot}
        />
      ),
    }),
    col.accessor((r) => formatAuditSnapshotSummary(r.afterSnapshot), {
      id: 'after',
      header: 'After',
      cell: (i) => (
        <AuditSnapshotCell
          testId={`audit-after-${i.row.original.auditId}`}
          snapshot={i.row.original.afterSnapshot}
          isAfter={true}
        />
      ),
    }),
  ];
}

export const AUDIT_COLUMNS = buildAuditColumns();
